/** Sequences in the workspace document (spec §7.5): ordered collections of blocks. */
import { LoroMap, LoroMovableList, LoroText, type LoroDoc } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";

export interface SequenceMember {
  memberId: string;
  target: string;
}
export interface Sequence {
  id: string;
  label: string;
  members: SequenceMember[];
}

function sequencesMap(doc: LoroDoc): LoroMap {
  return doc.getMap("sequences");
}

function membersList(entry: LoroMap): LoroMovableList | null {
  const m = entry.get("members");
  return m instanceof LoroMovableList ? m : null;
}

export function readSequences(doc: LoroDoc): Sequence[] {
  const map = sequencesMap(doc);
  const out: Sequence[] = [];
  for (const id of map.keys()) {
    const entry = map.get(id);
    if (!(entry instanceof LoroMap)) continue;
    const label = entry.get("label");
    const members = membersList(entry);
    out.push({
      id,
      label: label instanceof LoroText ? label.toString() : String(label ?? ""),
      members: members ? (members.toArray() as SequenceMember[]).filter((m) => m && m.target) : [],
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export function createSequence(doc: LoroDoc, label: string): string {
  const id = uuidv7();
  const entry = sequencesMap(doc).setContainer(id, new LoroMap());
  entry.setContainer("label", new LoroText()).insert(0, label);
  entry.setContainer("members", new LoroMovableList());
  doc.commit();
  return id;
}

export function renameSequence(doc: LoroDoc, id: string, label: string): void {
  const entry = sequencesMap(doc).get(id);
  if (!(entry instanceof LoroMap)) return;
  const t = entry.get("label");
  if (t instanceof LoroText) t.update(label);
  else entry.setContainer("label", new LoroText()).insert(0, label);
  doc.commit();
}

export function deleteSequence(doc: LoroDoc, id: string): void {
  sequencesMap(doc).delete(id);
  doc.commit();
}

export function addMember(doc: LoroDoc, id: string, target: string): void {
  const entry = sequencesMap(doc).get(id);
  if (!(entry instanceof LoroMap)) return;
  const members = membersList(entry) ?? entry.setContainer("members", new LoroMovableList());
  if ((members.toArray() as SequenceMember[]).some((m) => m?.target === target)) return;
  members.push({ memberId: uuidv7(), target });
  doc.commit();
}

export function removeMember(doc: LoroDoc, id: string, index: number): void {
  const entry = sequencesMap(doc).get(id);
  const members = entry instanceof LoroMap ? membersList(entry) : null;
  members?.delete(index, 1);
  doc.commit();
}

/** Reorder with the movable list's native move (member identity and order survive merges). */
export function moveMember(doc: LoroDoc, id: string, from: number, to: number): void {
  const entry = sequencesMap(doc).get(id);
  const members = entry instanceof LoroMap ? membersList(entry) : null;
  if (!members || from === to) return;
  members.move(from, to);
  doc.commit();
}
